import React, { useState, useEffect } from "react";
import "antd/dist/antd.css";
// import "antd/dist/reset.css";
import { Row, Card, Col, Popconfirm, Tooltip, Skeleton, Space, Divider, Popover } from "antd";
import { EditOutlined, DeleteOutlined, FormOutlined } from "@ant-design/icons";
import FormBuilder from "../creator/FormCreatorComponent";
import FormService from "../networkservice/FormService";
import FormRenderer from "../renderer/FormRenderer";
import CreateFormCard from "./CreateFormCard";
import "./listform.css";
import { useNavigate } from "react-router-dom";
import { RoleToLinkMapper } from "../../../../experience/utils/UrlUtils";
import { useGetRole } from "../../../../experience/generic/hooks/useGetRole";
import { useRecoilValue } from "recoil";
import { currentConfiguration } from "../../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../../../experience/utils/configurationconstants";
import { Chip } from "@mui/material";
import BackButton from "../../../../experience/generic/elements/backbutton/BackButton";

const ListFormComponent = ({ organizationId, formsBucketName, formSubmitCallback, userIdentifier }) => {
  const { Meta } = Card;
  const [formData, setFormData] = useState({});
  const [shouldSubmitFormData, setShouldSubmitFormData] = useState(false);
  const [shouldEditForm, setShouldEditForm] = useState(false);
  // const [deleteFormm, setDeleteFormm] = useState(false);
  const [allFormsData, setAllForms] = useState([]);
  const [templateCode, setTemplateCode] = useState("");
  const [userData, setUserData] = useState([]);
  const formService = new FormService();
  const [isDataLoading, setDataLoading] = useState(true);
  const role = useGetRole();
  const navigate = useNavigate();

  //Feature access settings
  const configuration = useRecoilValue(currentConfiguration);
  const [isCreateFormAllowed, setIsCreateFormAllowed] = useState(false);
  const [isEditFormAllowed, setIsEditFormAllowed] = useState(false);
  const [isDeleteFormAllowed, setIsDeleteFormAllowed] = useState(false);
  const [isSubmitFormAllowed, setIsSubmitFormAllowed] = useState(false);

  useEffect(() => {
    const configurationCreateForm = configuration[ConfigurationConstants.FEATUREACCESS_FORMBUILDER_CREATE_FORM];
    setIsCreateFormAllowed(configurationCreateForm?.visibility);

    const configurationEditForm = configuration[ConfigurationConstants.FEATUREACCESS_FORMBUILDER_EDIT_FORM];
    setIsEditFormAllowed(configurationEditForm?.visibility);

    const configurationDeleteForm = configuration[ConfigurationConstants.FEATUREACCESS_FORMBUILDER_DELETE_FORM];
    setIsDeleteFormAllowed(configurationDeleteForm?.visibility);

    const configurationSubmitForm = configuration[ConfigurationConstants.FEATUREACCESS_FORMBUILDER_SUBMIT_FORM];
    setIsSubmitFormAllowed(configurationSubmitForm?.visibility);

  }, [configuration])

  useEffect(() => {
    getForms();
  }, [organizationId]);


  /**
   * This function is use to get the forms based on Organization from the API.
   */
  async function getForms() {
    try {
      setDataLoading(true);
      let allForms = [];
      const forms = await formService.getAllFormMetaData(organizationId);
      if (forms.data) {
        for (let index = 0; index < forms.data.length; index++) {
          allForms.push(forms.data[index]);
        }
      }
      setDataLoading(false);
      setAllForms(allForms);
    }
    catch (error) { }
  }

  /**
   * This function is used to edit the form.
   */
  async function editForm(form) {
    setDataLoading(true);
    const formFromTemplate = await formService.getContentId(form.templateCode);
    const formJson = await formService.getJsonFromObjectStore(formFromTemplate.data.contentId, formsBucketName);
    setUserData([form.title, form.description, form.namespace, form.status, form.metadata, form.category, form.countTowards5percent]);
    setFormData(formJson);
    setShouldEditForm(true);
    setTemplateCode(form.templateCode);
    setDataLoading(false);
  }

  async function submitFormData(form) {
    setShouldSubmitFormData(true);
    setTemplateCode(form.templateCode);
    navigate(`${RoleToLinkMapper(role)}form-viewer?templateCode=${form.templateCode}&workforceIdentifier=${userIdentifier}`)
  }

  /*
   * This function delete the existing form from the storage
   */
  async function deleteForm(formToBeDeleted) {
    setDataLoading(true);
    await formService.getContentId(formToBeDeleted.templateCode);
    await formService.deleteFormTemplate(organizationId, "2tru9", formToBeDeleted.title, formToBeDeleted.description, formToBeDeleted.contentId, formToBeDeleted.templateCode);
    getForms();
    //TODO: Need to reload the page and need to fix hanging issue
  }

  function afterFormEdit() {
    setShouldEditForm(false);
    setTemplateCode("");
    getForms();
  }

  // function afterFormEdit(willCreate, receivedForm, isTouched) {
  //   setShouldEditForm(false);
  //   setTemplateCode("");
  //   getForms();
  // }

  const formActionsAllowed = (item) => {
    const formActions = [
      isDeleteFormAllowed &&
      <Popconfirm
        title="Are you sure want to delete?"
        onConfirm={() => {
          deleteForm(item);
          // setDeleteFormm(true);
        }}
        okButtonProps={{
          loading: isDataLoading,
        }}
        key="deleteForm"
      >
        <Tooltip placement="bottom" title="Delete">
          <DeleteOutlined key="delete" style={{color: "var(--secondary-theme-color)"}} />
        </Tooltip>
      </Popconfirm>,
      isEditFormAllowed &&
      <Tooltip placement="bottom" title="Edit" key="editForm">
        <EditOutlined key="edit" style={{color: "var(--secondary-theme-color)"}} onClick={() => editForm(item)} />
      </Tooltip>,
      isSubmitFormAllowed &&
      <Tooltip placement="bottom" title="Submit" key="submitForm">
        <FormOutlined key="submit" style={{color: "var(--secondary-theme-color)"}} onClick={() => submitFormData(item)} />
      </Tooltip>
    ].filter(Boolean);

    return formActions;
  }

  const content = (item) => {
    return (
      <div className="form-list-popover">
        {item && JSON.parse(item).map((data) => (
          <Chip key={data} label={data} className="form-chip" />
        ))}
      </div>
    )
  }

  return (
    <div>
      {shouldEditForm ? (
        <FormBuilder initialFormData={formData} onFormSubmit={afterFormEdit} shouldEditForm={shouldEditForm} templateCode={templateCode} userData={userData} setShouldEditForm={setShouldEditForm} />
      ) : shouldSubmitFormData ? (
        <FormRenderer templateCode={templateCode} organizationId={organizationId} formSubmitCallback={formSubmitCallback} />
      ) : (
        <>
          <div className="form-renderer-back-button">
            <BackButton />
            <p>Forms</p>
          </div>
          <Row gutter={16}>
            {isCreateFormAllowed && <CreateFormCard />}
            {!isDataLoading
              ? React.Children.toArray(
                allFormsData.map((item) => (
                  <Col className="formlist-column" xs={24} md={12} xl={8}>
                    <Card
                      className="formlist-column-card form-item-style"
                      actions={formActionsAllowed(item)}>
                      <Meta
                        className="form-data"
                        title={
                          <div className="form-data-title-section">
                            <h4 className="form-data-title">{item.title}</h4>
                            <div className="form-data-metadata-section">
                              {typeof item.countTowards5percent === "boolean" && <p>Counts toward 5% ?: <span>{item.countTowards5percent ? "Yes" : "No"}</span></p>}
                              {item.status && <p>Status: <Tooltip placement="bottom" title={item.status}><span>{item.status.charAt(0)}</span></Tooltip></p>}
                              {/* {item.namespace && <p>Type: <Tooltip placement="bottom" title={item.namespace}><span>{item.namespace.charAt(0)}</span></Tooltip></p>} */}
                            </div>
                          </div>
                        } description={
                          <div className="meta-description">
                            <p>{item.description}</p>
                            <div className="form-tags">
                              {(item.metadata && JSON.parse(item.metadata)["attributes"])?.map((data, index) => (
                                (index < 2) ? <Chip key={index} label={data} className="form-chip" /> : null
                              ))}
                              {item.metadata && JSON.parse(item.metadata).length > 2 && <Popover content={content(item.metadata)} trigger="click" placement="bottom"><Chip label={`+${JSON.parse(item.metadata).length - 2}`} className="form-chip" /></Popover>}
                            </div>
                          </div>
                        }
                      />
                    </Card>
                  </Col>
                )))
              : React.Children.toArray(
                Array.from({ length: 11 }).map(() => (
                  <Col xs={24} sm={16} md={12} lg={8}>
                    <Card style={{ marginTop: 16, height: 166 }}>
                      <Skeleton active paragraph={{ rows: 2 }} title={{ width: "100%" }} className="form-skeleton" />
                      <Divider style={{ margin: "6px 0" }} />
                      <Space style={{ justifyContent: "space-around", display: "flex", marginTop: 10 }}>
                        {isDeleteFormAllowed && <Skeleton.Avatar active size="small" />}
                        {isEditFormAllowed && <Skeleton.Avatar active size="small" />}
                        {isSubmitFormAllowed && <Skeleton.Avatar active size="small" />}
                      </Space>
                    </Card>
                  </Col>
                ))
              )}
          </Row>
        </>
      )}
    </div>
  );
};

export default ListFormComponent;